import { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { ImTv } from "react-icons/im";
import { fetchWithAuth } from "../api/fetchWithAuth";
import EraFeatureFormModal from "../components/EraFeatureFormModal";
import EraFeatureModal from "../components/EraFeatureModal";

export default function EraFeatures() {
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("ft_number");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    loadFeatures();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const loadFeatures = async () => {
    try {
      const res = await fetchWithAuth("/api/era-features");

      if (!res.ok) {
        console.error("Failed to load ERA features");
        return;
      }

      const data = await res.json();
      setFeatures(data);
    } catch (err) {
      console.warn("loadFeatures aborted:", err.message);
    }
  };

  const deleteFeature = async (ftNumber) => {
    if (!window.confirm("Delete this ERA feature?")) return;

    const res = await fetchWithAuth(`/api/era-features/${encodeURIComponent(ftNumber)}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      alert("Delete failed");
      return;
    }

    loadFeatures();
  };

  const openFeature = (feature) => {
    setSelectedFeature(feature);
    setShowModal(true);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filteredFeatures = features.filter((feature) => {
    const query = debouncedSearch.trim().toLowerCase();

    if (!query) return true;

    const number = String(feature.ft_number ?? "").toLowerCase();
    const title = String(feature.ft_title ?? "").toLowerCase();

    return number.includes(query) || title.includes(query);
  });

  const sortedFeatures = [...filteredFeatures].sort((a, b) => {
    const direction = sortDir === "asc" ? 1 : -1;
    const numberA = String(a.ft_number ?? "");
    const numberB = String(b.ft_number ?? "");

    if (sortField === "ft_number") {
      if (numberA < numberB) return -1 * direction;
      if (numberA > numberB) return 1 * direction;
      return 0;
    }

    const valueA = String(a[sortField] ?? "").toLowerCase();
    const valueB = String(b[sortField] ?? "").toLowerCase();

    if (valueA < valueB) return -1 * direction;
    if (valueA > valueB) return 1 * direction;
    if (numberA < numberB) return -1;
    if (numberA > numberB) return 1;
    return 0;
  });

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="position-relative w-25">
          <input
            type="text"
            className="form-control pe-5"
            placeholder="Search ERA features..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
              }}
              aria-label="Clear search"
            >
              x
            </button>
          )}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="badge text-bg-secondary">
            Total: {filteredFeatures.length}
          </span>
          <button
            className="btn btn-success"
            onClick={() => {
              setEditingFeature(null);
              setShowForm(true);
            }}
          >
            Add feature
          </button>
        </div>
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th
              style={{ ...stickyHeaderStyle, cursor: "pointer", width: "180px" }}
              onClick={() => handleSort("ft_number")}
            >
              <span className="d-inline-flex align-items-center">
                Number
                {renderSortIndicator("ft_number")}
              </span>
            </th>
            <th
              style={{ ...stickyHeaderStyle, cursor: "pointer" }}
              onClick={() => handleSort("ft_title")}
            >
              <span className="d-inline-flex align-items-center">
                Title
                {renderSortIndicator("ft_title")}
              </span>
            </th>
            <th style={{ ...stickyHeaderStyle, width: "120px" }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {sortedFeatures.map((feature, index) => (
            <tr key={feature.ft_number} style={getRowStyle(index)}>
              <td>
                <button
                  className="btn btn-link p-0 fw-bold"
                  onClick={() => openFeature(feature)}
                >
                  {renderHighlightedText(feature.ft_number)}
                </button>
              </td>
              <td>
                <div className="d-flex align-items-start gap-2">
                  {feature.hasDmi && (
                    <span
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0 text-primary"
                      style={{
                        width: "22px",
                        height: "22px",
                        marginTop: "1px"
                      }}
                      aria-label="Contains DMI"
                      title="Contains DMI"
                    >
                      <ImTv size={13} />
                    </span>
                  )}
                  <span>{renderHighlightedText(feature.ft_title)}</span>
                </div>
              </td>
              <td>
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={() => {
                    setEditingFeature(feature);
                    setShowForm(true);
                  }}
                >
                  <FaEdit />
                </button>

                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteFeature(feature.ft_number)}
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <EraFeatureFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadFeatures}
        feature={editingFeature}
      />

      <EraFeatureModal
        show={showModal}
        onClose={() => setShowModal(false)}
        feature={selectedFeature}
      />
    </div>
  );
}
